import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import iconRetry from "src/assets/images/icons_medium_retry.svg";
import { useSearchParams } from "react-router-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { CommonFacilityModel, DeskGroupModel } from "src/api/building/building-types";
import { getDeskgroupsAsync, getDesksAsync } from "src/api/building/deskgroup-api";
import { useApiOperation } from "src/api/hooks";
import { BaseBottomSheet, ConfirmModal, ResizedImage } from "src/components";
import MetaTag from "src/components/layout/MetaTag";
import usePostMessage from "../hooks/post-message";

// 예약가능한 좌석그룹
type AvailableDeskGroup = {
  deskGroupId: string; // 좌석그룹 id
  availableDeskIds: string; // 해당 좌석그룹에 예약가능한 좌석 id 들 (콤마로 구분)
  selectedDeskId?: string; // 예약가능한 좌석 id 들 중, 예약되어있는 좌석 id
};

type Modal = {
  isOpen: boolean;
  message?: string;
};

// zoom scale 최소 값
const TRANSFORM_MIN_SCALE = 0.6;

// {위치} {좌석 그룹명}
function getDeskGroupLabel(deskGroupModel?: DeskGroupModel | null): string {
  if (!deskGroupModel) return "";
  let label = "";
  label += deskGroupModel?.isGround ? `${Number(deskGroupModel?.floorNum || 0)}F` : `B${Number(deskGroupModel?.floorNum || 0)}`;
  label += " " + deskGroupModel?.groupName;
  return label;
}

/**
 * 좌석배치도 Taap 웹뷰 화면
 */
const SeatingChart = () => {
  // window.court.webViewFunction.setAvailableDeskGroups 초기화 여부 ref
  const initializedRef = useRef<boolean>(false);
  // 좌석 그룹 목록 조회 여부 ref
  const fetchDeskgroupsRef = useRef<boolean>(false);
  // 좌석배치도 img ref
  const imgRef = useRef<HTMLImageElement>(null);
  // TransformWrapper ref
  const transformWrapperRef = useRef(null);

  // (무인증) 좌석 그룹 목록 조회 api
  const { executeAsync: getDeskgroups } = useApiOperation(getDeskgroupsAsync, { noAuthenticationRequired: true, noHandleError: true });

  // (무인증) 좌석 그룹별 좌석 목록 조회 api
  const { executeAsync: getDesks } = useApiOperation(getDesksAsync, { noAuthenticationRequired: true, noHandleError: true });

  // ReactNativeWebView postMessage 를 관리하는 hook
  const {
    postMessageInitializedSetAvailableDeskGroups,
    postMessageGoBackNavigation,
    postMessageSelectedDesk,
    postMessageSelectedUnavailableDesk,
    postMessageRefreshRequested,
  } = usePostMessage();

  // query parameter
  const [searchParams] = useSearchParams();

  // query parameter 건물 id
  const buildingId: string | null = searchParams.get("buildingId");

  // query parameter 좌석 그룹 id
  const deskGroupId: string | null = searchParams.get("deskGroupId");

  // query parameter 좌석배치도만 보여주고 싶을 경우 true
  const isOnlySeatingChart: boolean = searchParams.get("isOnlySeatingChart") === "true";

  /**
   * query parameter 예약 불가 좌석 탭을 RN 이 받아 처리할 수 있는지
   *
   * **구버전 RN 하위호환용이다.** 웹뷰는 배포되면 바로 최신이지만 앱은 구버전이 남는다.
   * 이 값이 없으면(= 구버전) 예전처럼 disabled 로 막는다 — 눌러도 안내를 띄울 상대가 없어서,
   * 열어두면 아무 반응 없는 버튼이 된다.
   */
  const canReportUnavailableTap: boolean = searchParams.get("unavailableTapEnabled") === "true";

  /**
   * 예약 현황을 마지막으로 받은 시각.
   *
   * 웹뷰가 받아오는 좌표·이미지가 아니라 **앱이 주입하는 예약 가능 여부** 기준이다.
   * 사용자가 알고 싶은 건 "지금 이 자리가 비었는지"의 최신성이기 때문이다.
   */
  const [dataUpdatedAt, setDataUpdatedAt] = useState<Date | null>(null);

  /**
   * 새로고침 중인지. 버튼 아이콘 회전에만 쓴다.
   *
   * 앱이 예약 현황을 다시 주입해 주면 멈춘다. 구버전 앱은 응답하지 않으므로
   * 타이머로도 반드시 풀어 준다 — 아니면 영원히 돈다.
   */
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * 이중 클릭 차단용 잠금.
   *
   * **state 로는 막을 수 없다.** setState 는 다음 렌더에 반영되므로, 같은 틱에 두 번 눌리면
   * 두 호출 모두 `isRefreshing === false` 를 보고 통과한다. ref 는 즉시 반영된다.
   * disabled 는 그 다음 렌더에 붙으므로 첫 클릭 직후의 틈을 이 잠금이 메운다.
   */
  const refreshLockRef = useRef<boolean>(false);

  const stopRefreshing = useCallback(() => {
    refreshLockRef.current = false;
    setIsRefreshing(false);

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => stopRefreshing, [stopRefreshing]);

  // window width
  const [innerWidth, setInnerWidth] = useState<number>(window.innerWidth);
  // 좌석 배치도 이미지 witdh
  const [imageWidth, setImageWidth] = useState<number>(0);

  // 좌석 그룹 목록
  const [deskGroups, setDeskGroups] = useState<DeskGroupModel[]>([]);

  // 좌석 목록
  const [deskList, setDeskList] = useState<CommonFacilityModel[]>([]);

  // 좌석그룹목록 bottom sheet 오픈 여부
  const [isOpenBottomSheet, setIsOpenBottomSheet] = useState<boolean>(false);

  // 선택되어있는 좌석그룹 id
  const [selectedDeskGroupId, setSelectedDeskGroupId] = useState<string>("");

  /**
   * 지금 보고 있는 좌석그룹.
   *
   * `setAvailableDeskGroupsCallback` 은 앱이 호출하는 전역 함수라 의존성을 늘리면
   * 재할당이 반복된다. 그래서 state 대신 ref 로 읽는다.
   */
  const selectedDeskGroupIdRef = useRef<string>("");
  useEffect(() => {
    selectedDeskGroupIdRef.current = selectedDeskGroupId;
  }, [selectedDeskGroupId]);

  // 선택한 좌석 id
  const [selectedDeskId, setSelectedDeskId] = useState<string>("");

  // 예약가능한 좌석그룹 목록
  const [availableDeskGroups, setAvailableDeskGroups] = useState<AvailableDeskGroup[]>([]);

  // 확인 버튼만 있는 알림 모달
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });

  // 좌석배치도 이미지 로드를 실패했을 경우 true
  const [isLoadErrorImage, setIsLoadErrorImage] = useState<boolean>(false);

  // zoom scale 계산
  const calculateScale = useCallback(
    (_innerWidth: number, _imageWidth: number) => {
      // 48 은 Taap 에서의 양쪽 padding (24 x 2)
      const windowWidth = isOnlySeatingChart ? _innerWidth - 48 : _innerWidth;

      // .base-layout-container max-width: 500px 보다 크면
      if (windowWidth >= 500) {
        return 0.9;
      }

      // 이미지 width 가 window width 보다 크면
      if (_imageWidth > windowWidth) {
        return Number((windowWidth / _imageWidth).toFixed(2));
      }

      // 이미지 width 가 window width 보다 작으면
      const scale = Number((_imageWidth / windowWidth).toFixed(2));
      // 최소 scale 보다 작으면
      if (scale < TRANSFORM_MIN_SCALE) {
        return TRANSFORM_MIN_SCALE;
      }

      return scale;
    },
    [isOnlySeatingChart],
  );

  // 좌석 그룹별 좌석 목록 조회
  const fetchDesks = useCallback(
    async (buildingId: string | number, deskGroupId: string | number) => {
      const { data } = await getDesks({ buildingId, deskGroupId });
      const _deskGroup = data?.data?.content || null;

      // 좌석 좌표가 있는 것만 필터링
      const filterdDeskList = (_deskGroup?.deskList || []).filter(
        (desk: CommonFacilityModel) => Number(desk?.centerX || 0) > 0 && Number(desk?.centerY || 0) > 0,
      );
      setDeskList(filterdDeskList);
    },
    [getDesks],
  );

  // 예약가능한 좌석그룹 목록 바인딩
  const setAvailableDeskGroupsCallback = useCallback(
    async (_availableDeskGroups?: string) => {
      if (!buildingId) throw Error("buildingId is not found");
      const receivedAvailableDeskGroups = _availableDeskGroups ? JSON.parse(_availableDeskGroups) : [];

      // 전체 좌석그룹목록
      const allDeskGroups = deskGroups || [];

      // 예약가능한 좌석그룹 목록
      const filteredAvailableDeskGroups: AvailableDeskGroup[] = [];
      receivedAvailableDeskGroups.forEach((a: AvailableDeskGroup) => {
        // 예약가능한 좌석그룹 중에서 전체 좌석그룹에서 존재하는 것만 필터링
        const find = allDeskGroups.find((d: DeskGroupModel) => a.deskGroupId === d.id);
        if (find) {
          filteredAvailableDeskGroups.push(a);
        }
      });
      setAvailableDeskGroups(filteredAvailableDeskGroups);
      setDataUpdatedAt(new Date());
      stopRefreshing();

      /*
       * 좌석그룹 선택.
       *
       * **지금 보고 있는 그룹을 가장 먼저 존중한다.** 이 콜백은 진입할 때뿐 아니라
       * 새로고침으로 예약 현황이 다시 주입될 때도 실행된다. 그때 URL 파라미터나 첫 그룹으로
       * 되돌리면, 3층을 보다가 새로고침했는데 1층으로 튀어버린다.
       */
      let selectDeskGroupId = "";
      if (filteredAvailableDeskGroups.length > 0) {
        const stillAvailable = filteredAvailableDeskGroups.some(
          (_availableDeskGroup) => String(_availableDeskGroup.deskGroupId) === selectedDeskGroupIdRef.current,
        );

        if (stillAvailable) {
          // 보던 그룹 유지
          selectDeskGroupId = selectedDeskGroupIdRef.current;
        } else if (deskGroupId) {
          // 진입 시 지정된 그룹
          selectDeskGroupId = deskGroupId;
        } else {
          // 선택한 좌석그룹이 없을 경우 첫번째 좌석그룹 선택
          selectDeskGroupId = filteredAvailableDeskGroups[0].deskGroupId;
        }
      }

      if (selectDeskGroupId) {
        setSelectedDeskGroupId(selectDeskGroupId);

        // 좌석 선택
        const findDeskGroup = filteredAvailableDeskGroups.find(
          (_availableDeskGroup) => String(_availableDeskGroup.deskGroupId) === selectDeskGroupId,
        );
        if (findDeskGroup) {
          /*
           * 앱이 알려준 선택 상태를 그대로 따른다.
           *
           * 예전에는 값이 있을 때만 표기하고 **지우지는 않았다.** 새로고침하면 앱은 선택을
           * 비우는데(기획: 리프레시 시 선택 리셋) 배치도에는 그대로 남아 있었다.
           */
          setSelectedDeskId(findDeskGroup.selectedDeskId ? String(findDeskGroup.selectedDeskId) : "");
        }
      }
    },
    [deskGroups, buildingId, deskGroupId, stopRefreshing],
  );

  const handleResize = () => {
    setInnerWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // function 초기화가 되어있지않고, 전체 좌석그룹 목록을 조회를 했다면
    // function 초기화하고, RN 에 post message initializedSetAvailableDeskGroups 전달
    if (!initializedRef.current && fetchDeskgroupsRef.current) {
      // setAvailableDeskGroups global function 초기화 (재할당)
      window.court.webViewFunction.setAvailableDeskGroups = setAvailableDeskGroupsCallback;
      postMessageInitializedSetAvailableDeskGroups();
      initializedRef.current = true;
    }
  }, [setAvailableDeskGroupsCallback, postMessageInitializedSetAvailableDeskGroups]);

  // 좌석 그룹 목록 조회
  const fetchDeskgroups = useCallback(
    async (buildingId: string | number) => {
      const { data } = await getDeskgroups({ buildingId });
      const _deskGroups = data?.data?.content || [];
      setDeskGroups(_deskGroups);
      fetchDeskgroupsRef.current = true;
    },
    [getDeskgroups],
  );

  // 좌석그룹 변경
  const onChangeSelectedDeskGroup = useCallback(
    async (_deskGroupId: string) => {
      if (buildingId) {
        setIsOpenBottomSheet(false);
        setSelectedDeskGroupId(_deskGroupId);
        setSelectedDeskId("");

        if (transformWrapperRef.current) {
          // zoom center 로 이동
          const scale = calculateScale(innerWidth, imageWidth);
          (transformWrapperRef.current as any)?.centerView(scale);
        }
      }
    },
    [buildingId, innerWidth, imageWidth],
  );

  useEffect(() => {
    // 건물 id 변경될 때
    if (buildingId) {
      // 해당 건물의 좌석그룹목록 조회
      fetchDeskgroups(buildingId);
    }
  }, [buildingId]);

  useEffect(() => {
    // 좌석그룹 선택이 변경될때
    if (buildingId && selectedDeskGroupId) {
      // 선택한 좌석그룹의 좌석목록 조회
      fetchDesks(buildingId, selectedDeskGroupId);
    }
  }, [buildingId, selectedDeskGroupId]);

  // 선택되어있는 좌석 그룹
  const selectedDeskGroup: DeskGroupModel | undefined = useMemo(() => {
    if (availableDeskGroups.length === 0) return undefined;
    // 예약가능한 좌석 그룹만 필터링
    const filteredAvailableDeskGroups = deskGroups.filter((deskGroup: DeskGroupModel) => {
      const availableDeskGroupIds = availableDeskGroups.map((v: AvailableDeskGroup) => v.deskGroupId);
      return availableDeskGroupIds.includes(deskGroup.id);
    });
    if (!selectedDeskGroupId) {
      // 선택되어있는 좌석그룹이 없을 경우 첫번째 좌석 그룹 선택
      return filteredAvailableDeskGroups[0];
    } else {
      return filteredAvailableDeskGroups.find((deskGroup: DeskGroupModel) => deskGroup.id === selectedDeskGroupId);
    }
  }, [selectedDeskGroupId, deskGroups, availableDeskGroups]);

  // 좌석그룹의 배치도 이미지 url
  const seatingChartImageUrl: string | undefined = useMemo(() => {
    return (selectedDeskGroup?.mediaList || [])[0]?.url;
  }, [selectedDeskGroup]);

  // 선택 버튼 disabled 여부
  const isDisabledSelectButton = useMemo(() => {
    return !selectedDeskId || deskList.length === 0 || !deskList.find((desk) => String(desk.id) === String(selectedDeskId));
  }, [selectedDeskId, deskList]);

  // 예약가능한 좌석 여부
  const isAvailableDesk = useCallback(
    (deskId: string) => {
      if (!availableDeskGroups || availableDeskGroups.length === 0) return false;
      const findDeskGroup = availableDeskGroups.find((v: AvailableDeskGroup) => String(v.deskGroupId) === String(selectedDeskGroupId));
      if (!findDeskGroup || !findDeskGroup?.availableDeskIds) return false;
      return findDeskGroup.availableDeskIds.split(",").includes(deskId);
    },
    [selectedDeskGroupId, availableDeskGroups],
  );

  /**
   * 새로고침.
   *
   * 좌석 좌표·배치도 이미지는 웹뷰가 받아오므로 여기서 다시 부른다.
   * 예약 가능 여부는 앱이 내려주는 값이라 웹뷰가 갱신할 수 없다 —
   * 그건 앱이 자기 새로고침으로 처리하고 다시 주입한다.
   */
  const onClickRefresh = useCallback(async () => {
    // 동작 중에는 다시 받지 않는다 (disabled 가 붙기 전의 연타까지 여기서 끊는다)
    if (refreshLockRef.current) {
      return;
    }
    refreshLockRef.current = true;

    /*
     * 선택 해제.
     *
     * **여기서 지워야 한다.** 좌석을 탭한 결과는 `선택` 을 누르기 전까지 이 화면의 로컬 상태라,
     * 앱은 아직 그 선택을 모른다. 앱이 재주입해 주기를 기다리면 아무 일도 일어나지 않는다.
     * 기획도 "리프레시 시점에 선택된 좌석을 리셋한다" 로 예외를 두지 않는다.
     */
    setSelectedDeskId("");

    setIsRefreshing(true);

    /*
     * 앱이 응답하지 않아도(구버전) 버튼이 계속 잠겨 있지 않도록 상한을 둔다.
     *
     * 3초로 잡았다. 이 시간이 지나면 다시 누를 수 있다 — 중복 요청이 나가더라도
     * 같은 조회라 앱에서 합쳐지므로, 눌리지 않는 버튼을 오래 두는 쪽이 더 나쁘다.
     */
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => {
      refreshLockRef.current = false;
      setIsRefreshing(false);
    }, 3000);

    // 앱이 예약 현황을 다시 조회해 주입한다. 주입되면 dataUpdatedAt 도 갱신된다
    postMessageRefreshRequested();

    // 좌표·배치도 이미지는 웹뷰가 받아오므로 여기서 다시 부른다
    if (buildingId && selectedDeskGroupId) {
      await fetchDesks(buildingId, selectedDeskGroupId);
    }
  }, [buildingId, selectedDeskGroupId, fetchDesks, postMessageRefreshRequested]);

  // 선택버튼 클릭
  const onClickSelect = useCallback(() => {
    if (!selectedDeskGroupId) {
      // 좌석그룹 선택되어있지 않은 경우 웹뷰 닫기
      postMessageGoBackNavigation();
      return;
    }

    if (!selectedDeskId) {
      setAlertModal({ isOpen: true, message: "좌석을 선택해주세요" });
      return;
    }

    // RN 에 선택한 좌석그룹, 좌석 정보 전달
    postMessageSelectedDesk({
      deskGroupId: selectedDeskGroupId,
      deskId: selectedDeskId,
    });
  }, [selectedDeskId, selectedDeskGroupId, postMessageGoBackNavigation, postMessageSelectedDesk]);

  // 예약가능한 좌석그룹목록이 없을 경우
  if (availableDeskGroups.length === 0) return null;

  return (
    <div>
      <MetaTag title="좌석배치도 | TaapSpace" />

      {/* 좌석배치도만 보여주고 싶을 경우, 헤더 영역은 그려주지 않는다 */}
      {!isOnlySeatingChart && (
        <section className="back-header-wrap">
          <div className="back-header">
            <div className="back-header__left">
              <button
                type="button"
                className="back-btn"
                onClick={() => {
                  postMessageGoBackNavigation();
                }}
              ></button>
            </div>
            <button
              type="button"
              className={`chevron-down-btn ellipsis ${isOpenBottomSheet ? "--active" : ""}`}
              disabled={availableDeskGroups.length === 0 || availableDeskGroups.length === 1}
              onClick={() => {
                setIsOpenBottomSheet(true);
              }}
            >
              {getDeskGroupLabel(selectedDeskGroup)}
            </button>
            {availableDeskGroups.length > 0 && (
              <div className="back-header__right">
                <button type="button" className="header-text-btn" onClick={() => onClickSelect()} disabled={isDisabledSelectButton}>
                  선택
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/*
        새로고침 행. **웹뷰가 그린다.**
        헤더(뒤로가기·선택)가 웹뷰 안에 있어서, 시안대로 '선택' 버튼 아래에 놓으려면
        여기가 아니면 안 된다. 앱은 이 버튼을 그리지 않고 눌렸다는 메시지만 받는다.
      */}
      {!isOnlySeatingChart && availableDeskGroups.length > 0 && (
        <div className="seating-chart-refresh">
          {/*
            갱신 중에는 시각 자리를 스켈레톤으로 바꾼다.
            아직 한 번도 받지 못한 상태도 로딩으로 본다 — 시각이 없는 건지 오는 중인지
            구분이 안 되기 때문이다.

            텍스트에 덮지 않고 **별도 요소로 교체**한다. 글자 위에 덮으면 크기가 글자 폭을
            따라가서, 문구가 짧은 최초 상태와 시각이 들어온 뒤의 크기가 달라진다.
          */}
          {isRefreshing || !dataUpdatedAt ? (
            <span className="seating-chart-refresh__skeleton" aria-hidden="true" />
          ) : (
            <span className="seating-chart-refresh__time">
              {`${String(dataUpdatedAt.getHours()).padStart(2, "0")}:${String(dataUpdatedAt.getMinutes()).padStart(2, "0")} 업데이트`}
            </span>
          )}
          <button
            type="button"
            className={`seating-chart-refresh__btn ${isRefreshing ? "--spinning" : ""}`}
            onClick={onClickRefresh}
            disabled={isRefreshing}
            aria-label="새로고침"
          >
            {/* 원까지 포함된 아이콘이라 색을 입히지 않고 그대로 쓴다 */}
            <img src={iconRetry} alt="" width={24} height={24} />
          </button>
        </div>
      )}

      {seatingChartImageUrl ? (
        // 좌석배치도 이미지 있는 경우
        <>
          {isLoadErrorImage ? (
            // 좌석배치도 이미지 로딩하다가 오류 발생한 경우
            <div className="seat-chart">
              <section>
                <div className="seat-container-zoom with-header">
                  <div className="snack">
                    <div className="ic-snack"></div>
                    <h2>Play work on Taap</h2>
                    <p>오늘도 응원할게요!!</p>
                  </div>
                </div>
              </section>
            </div>
          ) : (
            // 좌석배치도 이미지 로딩 성공한 경우
            <TransformWrapper doubleClick={{ disabled: true }} minScale={TRANSFORM_MIN_SCALE} maxScale={2} ref={transformWrapperRef}>
              {({ centerView }) => (
                <div className="seat-chart">
                  <section>
                    <div className={`seat-container-zoom ${isOnlySeatingChart ? "" : "with-header"}`}>
                      <TransformComponent>
                        <div className="seat-container">
                          {/* 좌석 컴포넌트들 */}
                          {deskList.map((desk: CommonFacilityModel) => {
                            // 좌석배치도만 보여주는 모드에서는 예약 가능 여부를 따지지 않는다
                            const unavailable = isOnlySeatingChart ? false : !isAvailableDesk(String(desk.id));

                            return (
                              <button
                                type="button"
                                className={`desc-comp-btn ${String(selectedDeskId) === String(desk.id) ? "--active" : ""} ${
                                  unavailable ? "--unavailable" : ""
                                }`}
                                key={desk.id}
                                style={{ left: Number(desk.centerX || 0), top: Number(desk.centerY || 0) }}
                                onClick={() => {
                                  /*
                                   * 예약할 수 없는 좌석도 눌리게 둔다.
                                   *
                                   * 예전에는 disabled 로 막아서 클릭 이벤트 자체가 발생하지 않았고,
                                   * 그래서 왜 못 고르는지 알려줄 방법이 없었다.
                                   * 이제는 RN 에 올려보내고, 사유 판정과 안내 문구는 RN 이 맡는다.
                                   */
                                  if (unavailable) {
                                    // 구버전 RN 에서는 여기까지 오지 않는다 (disabled 로 막힌다)
                                    postMessageSelectedUnavailableDesk({
                                      deskGroupId: String(selectedDeskGroupId),
                                      deskId: String(desk.id),
                                    });
                                    return;
                                  }

                                  if (!isOnlySeatingChart) {
                                    // 좌석배치도만 보여주고 싶을 경우를 제외하고, 좌석 선택 가능하게
                                    setSelectedDeskId(String(desk.id));
                                  }
                                }}
                                /*
                                 * 신버전 RN 에서만 눌리게 연다. 구버전에서는 disabled 로 막아
                                 * 지금까지와 똑같이 동작한다 (탭해도 아무 일 없음).
                                 */
                                disabled={unavailable && !canReportUnavailableTap}
                              >
                                {desk.facilityName}
                              </button>
                            );
                          })}

                          <ResizedImage
                            ref={imgRef}
                            url={seatingChartImageUrl}
                            alt="좌석배치도 이미지"
                            onLoad={() => {
                              if (imgRef.current) {
                                // 이미지가 로드 되었을때, 이미지 width 구하기
                                const _imageWidth = imgRef.current.clientWidth;
                                setImageWidth(_imageWidth);

                                // 이미지 width 로 계산된 zoom scale 계산
                                const scale = calculateScale(innerWidth, _imageWidth);
                                centerView(scale);
                              }
                            }}
                            onError={(e) => {
                              // 좌석 배치도 이미지 로드 실패
                              setIsLoadErrorImage(true);
                            }}
                          />
                        </div>
                      </TransformComponent>
                    </div>
                  </section>
                </div>
              )}
            </TransformWrapper>
          )}
        </>
      ) : (
        // 좌석배치도 이미지 없는 경우
        <div className="error-page with-header">
          <section>
            <div className="error-icon ic-none"></div>
            <div className="error-message-wrap">
              <h2>해당 배치도를 이용할 수 없습니다</h2>
              <p>리스트에서 좌석을 선택해 주세요</p>
            </div>
          </section>
        </div>
      )}

      {/* 좌석그룹 선택 바텀시트 */}
      <BaseBottomSheet isOpen={isOpenBottomSheet} onClose={() => setIsOpenBottomSheet(false)} title={"구역을 선택해주세요"} className="scrollable">
        <div className="section-select-sheet">
          <ul className="section-list">
            {availableDeskGroups.map((deskGroup: AvailableDeskGroup, index: number) => (
              <li
                key={index.toString()}
                className={`section-list__item ${deskGroup.deskGroupId === selectedDeskGroupId ? "--active" : ""}`}
                onClick={() => {
                  onChangeSelectedDeskGroup(deskGroup.deskGroupId);
                }}
              >
                {getDeskGroupLabel(deskGroups.find((d: DeskGroupModel) => d.id === deskGroup.deskGroupId))}
              </li>
            ))}
          </ul>
        </div>
      </BaseBottomSheet>

      <ConfirmModal
        isOpen={alertModal.isOpen}
        btnRightTitle="확인"
        onClick={() => {
          setAlertModal({ isOpen: false, message: "" });
        }}
        children={
          <div>
            <p className="font18 font-weight-600">{alertModal.message}</p>
          </div>
        }
      />
    </div>
  );
};

export default SeatingChart;
